// Core
import React from 'react';

// Libraries
import { useTranslation } from 'react-i18next';

// Plugins
import {
  booleanToSolenoidStatus,
} from '../../../../../../../../plugins/crusher';

// Types
import { SolenoidStatus } from '../../../../../../../../types/crusher';

// Components
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../../../layout/elements/Paragraph';

// Media
import ValveBankImage from '../../../../../../../../media/images/maintenance/ComboUnit/ValveBank.avif';

// View
import {
  SolenoidData,
  ValveBankProps,
} from './ValveBank.types';
import {
  ComboUnitImageContainer,
  ComboUnitImageStyled,
  Container,
  ContentContainer,
  LeftPanel,
  RightPanel,
  SolenoidStatusContainer,
  SolenoidStatusTitle,
  TitleSection,
  NumberedCell,
  SolenoidTable,
  SolenoidTableRow,
  SolenoidFunctionCell,
  SolenoidStatusCell,
  SolenoidStatusDot,
} from './ValveBank.style';

function ValveBank({
  className,
  unitData,
  edgeStatus,
  testId,
}: ValveBankProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();

  // Solenoid data array with functions and their status
  // Using real PLC data from EquipmentUnitDataConeCrusher
  // Each function shows only the specific solenoids that are active for that function
  const solenoidData: SolenoidData[] = [
    {
      key: 'trampPressurize',
      name: t('view.crusher.maintenance.comboUnit.valveBank.trampPressurize'),
      solenoids: [
        {
          id: 1,
          status: booleanToSolenoidStatus(
            unitData?.dOutputHydSol1,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          ),
        },
        {
          id: 2,
          status: booleanToSolenoidStatus(
            unitData?.dOutputHydSol2,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          ),
        },
        { id: 3, status: SolenoidStatus.disabled },
        { id: 4, status: SolenoidStatus.disabled },
        { id: 5, status: SolenoidStatus.disabled },
        { id: 6, status: SolenoidStatus.disabled },
        { id: 7, status: SolenoidStatus.disabled },
        { id: 8, status: SolenoidStatus.disabled },
      ],
    },
    {
      key: 'clearing',
      name: t('view.crusher.maintenance.comboUnit.valveBank.clearing'),
      solenoids: [
        {
          id: 1,
          status: booleanToSolenoidStatus(
            unitData?.dOutputHydSol1,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          ),
        },
        { id: 2, status: SolenoidStatus.disabled },
        {
          id: 3,
          status: booleanToSolenoidStatus(
            unitData?.dOutputHydSol3,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          ),
        },
        { id: 4, status: SolenoidStatus.disabled },
        { id: 5, status: SolenoidStatus.disabled },
        { id: 6, status: SolenoidStatus.disabled },
        { id: 7, status: SolenoidStatus.disabled },
        { id: 8, status: SolenoidStatus.disabled },
      ],
    },
    {
      key: 'openNoLoad',
      name: t('view.crusher.maintenance.comboUnit.valveBank.openNoLoad'),
      solenoids: [
        {
          id: 1,
          status: booleanToSolenoidStatus(
            unitData?.dOutputHydSol1,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          ),
        },
        { id: 2, status: SolenoidStatus.disabled },
        { id: 3, status: SolenoidStatus.disabled },
        { id: 4, status: SolenoidStatus.disabled },
        {
          id: 5,
          status: booleanToSolenoidStatus(
            unitData?.dOutputHydSol5,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          ),
        },
        { id: 6, status: SolenoidStatus.disabled },
        { id: 7, status: SolenoidStatus.disabled },
        {
          id: 8,
          status: booleanToSolenoidStatus(
            unitData?.dOutputHydSol8,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          ),
        },
      ],
    },
    {
      key: 'closeNoLoad',
      name: t('view.crusher.maintenance.comboUnit.valveBank.closeNoLoad'),
      solenoids: [
        {
          id: 1,
          status: booleanToSolenoidStatus(
            unitData?.dOutputHydSol1,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          ),
        },
        { id: 2, status: SolenoidStatus.disabled },
        { id: 3, status: SolenoidStatus.disabled },
        { id: 4, status: SolenoidStatus.disabled },
        {
          id: 5,
          status: booleanToSolenoidStatus(
            unitData?.dOutputHydSol5,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          ),
        },
        { id: 6, status: SolenoidStatus.disabled },
        {
          id: 7,
          status: booleanToSolenoidStatus(
            unitData?.dOutputHydSol7,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          ),
        },
        { id: 8, status: SolenoidStatus.disabled },
      ],
    },
    {
      key: 'openLoad',
      name: t('view.crusher.maintenance.comboUnit.valveBank.openLoad'),
      solenoids: [
        {
          id: 1,
          status: booleanToSolenoidStatus(
            unitData?.dOutputHydSol1,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          ),
        },
        { id: 2, status: SolenoidStatus.disabled },
        { id: 3, status: SolenoidStatus.disabled },
        {
          id: 4,
          status: booleanToSolenoidStatus(
            unitData?.dOutputHydSol4,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          ),
        },
        {
          id: 5,
          status: booleanToSolenoidStatus(
            unitData?.dOutputHydSol5,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          ),
        },
        {
          id: 6,
          status: booleanToSolenoidStatus(
            unitData?.dOutputHydSol6,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          ),
        },
        { id: 7, status: SolenoidStatus.disabled },
        {
          id: 8,
          status: booleanToSolenoidStatus(
            unitData?.dOutputHydSol8,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          ),
        },
      ],
    },
    {
      key: 'closeLoad',
      name: t('view.crusher.maintenance.comboUnit.valveBank.closeLoad'),
      solenoids: [
        {
          id: 1,
          status: booleanToSolenoidStatus(
            unitData?.dOutputHydSol1,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          ),
        },
        { id: 2, status: SolenoidStatus.disabled },
        { id: 3, status: SolenoidStatus.disabled },
        {
          id: 4,
          status: booleanToSolenoidStatus(
            unitData?.dOutputHydSol4,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          ),
        },
        {
          id: 5,
          status: booleanToSolenoidStatus(
            unitData?.dOutputHydSol5,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          ),
        },
        {
          id: 6,
          status: booleanToSolenoidStatus(
            unitData?.dOutputHydSol6,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          ),
        },
        {
          id: 7,
          status: booleanToSolenoidStatus(
            unitData?.dOutputHydSol7,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          ),
        },
        { id: 8, status: SolenoidStatus.disabled },
      ],
    },
    {
      key: 'clampPressurize',
      name: t('view.crusher.maintenance.comboUnit.valveBank.clampPressurize'),
      solenoids: [
        { id: 1, status: SolenoidStatus.disabled },
        { id: 2, status: SolenoidStatus.disabled },
        { id: 3, status: SolenoidStatus.disabled },
        {
          id: 4,
          status: booleanToSolenoidStatus(
            unitData?.dOutputHydSol4,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          ),
        },
        { id: 5, status: SolenoidStatus.disabled },
        { id: 6, status: SolenoidStatus.disabled },
        { id: 7, status: SolenoidStatus.disabled },
        { id: 8, status: SolenoidStatus.disabled },
      ],
    },
  ];

  return (
    <Container
      className={className}
      data-testid={testId}
    >
      <ContentContainer>
        <LeftPanel>
          <SolenoidStatusContainer>
            <SolenoidStatusTitle>
              <TitleSection>
                <Paragraph
                  size={ParagraphSize.sm}
                  weight={ParagraphWeight.bold}
                >
                  {t('view.crusher.maintenance.comboUnit.valveBank.solenoidStatus')}
                </Paragraph>
              </TitleSection>

              {[1, 2, 3, 4, 5, 6, 7, 8].map((solenoidNumber) => (
                <NumberedCell key={solenoidNumber}>
                  <Paragraph
                    size={ParagraphSize.sm}
                    weight={ParagraphWeight.bold}
                  >
                    {solenoidNumber}
                  </Paragraph>
                </NumberedCell>
              ))}
            </SolenoidStatusTitle>

            <SolenoidTable>
              {solenoidData.map((solenoidFunction) => (
                <SolenoidTableRow key={solenoidFunction.key}>
                  <SolenoidFunctionCell>
                    <Paragraph
                      size={ParagraphSize.sm}
                      weight={ParagraphWeight.medium}
                    >
                      {solenoidFunction.name}
                    </Paragraph>
                  </SolenoidFunctionCell>

                  {solenoidFunction.solenoids.map((solenoid) => (
                    <SolenoidStatusCell key={solenoid.id}>
                      <SolenoidStatusDot
                        status={solenoid.status}
                      />
                    </SolenoidStatusCell>
                  ))}
                </SolenoidTableRow>
              ))}
            </SolenoidTable>
          </SolenoidStatusContainer>
        </LeftPanel>

        <RightPanel>
          <ComboUnitImageContainer>
            <ComboUnitImageStyled
              src={ValveBankImage}
              alt="Valve Bank"
              isHovered={false}
            />
          </ComboUnitImageContainer>
        </RightPanel>
      </ContentContainer>
    </Container>
  );
}

export { ValveBank };
